import { cn } from '@/lib/utils'
import { PageContainer } from '@/components/Container'
import { GameCard, GameSection, ComingSoonSection, ExternalGameWidget } from '@/components/Games'
import { PreviewCarousel } from '@/components/PreviewCarousel'

import { PlayerProgressStream } from '@/components/PlayerProgressStream'
import { WelcomeSection } from '@/components/WelcomeSection'
import { CTAWidget } from '@/components/CTAWidget'
import { PremiumUpsellModal } from '@/components/archive'
import { useAuth } from '@/hooks/useAuth'
import { useUser } from '@clerk/clerk-react'
import { useNavigate } from 'react-router-dom'
import { useGameRegistry } from '@/hooks/useGameRegistry'
import { useSections } from '@/hooks/useSections'
import { useMemo, useState, useEffect, useCallback } from 'react'
import type { GameChallenge, ChallengeWithProgress, PlayerProgress, Game } from '@/types/game'
import type { SectionWithGames } from '@/types/section'
import { GameChallengePopup } from '@/components/GameChallengePopup'
import { GameWalkthroughModal } from '@/components/GameWalkthroughModal'
import { useGamePopupData } from '@/hooks/useGamePopupData'
import { sectionService } from '@/services/sectionService'
import { isExternalGame, handleExternalGameClick } from '@/services/externalGameService'
import { isContinuousGame, GameLaunchError } from '@/services/gameLaunchService'
import posthog from 'posthog-js'
import { getChallengeButtonText } from '@/utils/challengeButtonText'

export default function Games() {
  const currentSport = 'baseball'; // This would come from context/state
  const { requireAuth } = useAuth();
  const { user, isLoaded: isUserLoaded } = useUser();
  const navigate = useNavigate();
  const { games, loading, error, refreshGames, invalidateCache } = useGameRegistry();
  // IMA-88: Removed useSubscription - access control is now handled by backend
  // PlayerProgressStream receives accessControl from backend via challenge data
  const { sections, isLoading: sectionsLoading, error: sectionsError, refetch: refetchSections } = useSections();
  
  // State for popup (shared by GameChallengePopup and GameWalkthroughModal)
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [selectedChallenge, setSelectedChallenge] = useState<GameChallenge | null>(null);
  const [selectedProgress, setSelectedProgress] = useState<PlayerProgress | undefined>(undefined);
  // IMA-561: true when walkthrough modal should open instead of GameChallengePopup
  const [walkthroughActive, setWalkthroughActive] = useState(false);

  // Premium upsell modal state
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const [upsellGame, setUpsellGame] = useState<Game | null>(null);

  // D40: invalidate challenge cache for any game just played so progress data is fresh on return
  useEffect(() => {
    const justPlayedGameId = sessionStorage.getItem('justPlayedGame');
    if (justPlayedGameId) {
      sessionStorage.removeItem('justPlayedGame');
      invalidateCache(justPlayedGameId);
    }
  }, [invalidateCache]);

  // D39: today's challenge per game — populated by PlayerProgressStream via onTodayChallengeReady
  const [todayChallenges, setTodayChallenges] = useState<Record<string, ChallengeWithProgress>>({});
  // IMA-555: Controlled carousel index keyed by section.id — supports multiple preview sections
  const [previewIndexBySection, setPreviewIndexBySection] = useState<Record<number, number>>({});
  const handleTodayChallengeReady = useCallback((gameId: string, challenge: ChallengeWithProgress) => {
    setTodayChallenges(prev => {
      if (prev[gameId]?.id === challenge.id) return prev; // no-op if already set
      return { ...prev, [gameId]: challenge };
    });
  }, []);
  
  // Fetch data for the selected game popup
  const { 
    playerStats,
    isCurrentPeriod,
    loading: popupDataLoading,
    error: popupDataError,
    isAuthenticated,
    refetch: refetchPopupData
  } = useGamePopupData(selectedGame?.id || null, selectedChallenge?.id?.toString() || null);
  
  // Ensure data is fresh when popup opens
  useEffect(() => {
    if (selectedGame && selectedChallenge) {
      refetchPopupData();
      
      // Track analytics event
      posthog.capture('game_popup_opened', {
        game_id: selectedGame.id,
        game_name: selectedGame.displayName,
        challenge_id: selectedChallenge.id,
        challenge_type: selectedGame.challengeType,
        is_authenticated: isAuthenticated,
        has_progress: !!selectedProgress
      });
    }
  }, [selectedGame?.id, selectedChallenge?.id]);
  
  // Check for stored intent after authentication
  useEffect(() => {
    if (user && games.length > 0) {
      const intendedGameId = sessionStorage.getItem('intendedGame');
      const intendedChallengeId = sessionStorage.getItem('intendedChallenge');
      
      if (intendedGameId && intendedChallengeId) {
        // Clear the stored intent
        sessionStorage.removeItem('intendedGame');
        sessionStorage.removeItem('intendedChallenge');
        
        // Find the game and challenge
        const game = games.find(g => g.id === intendedGameId);
        const challengeIdNum = parseInt(intendedChallengeId);
        
        if (game && !isNaN(challengeIdNum)) {
          // User already confirmed intent before signing in — navigate directly with state.
          // Do not open a stub popup (stub has no displayState, causes "Premium Access Required").
          setTimeout(() => {
            sessionStorage.setItem('justPlayedGame', game.id);
            navigate(`/games/${game.id}/play/${challengeIdNum}`, { state: { game } });
          }, 100);
        }
      }
    }
  }, [user, games]);
  
  // Challenge click handlers (prep for Phase 3)
  const handleChallengeClick = (challenge: GameChallenge, progress?: PlayerProgress) => {
    // Find the game this challenge belongs to
    const game = games.find(g => g.id === challenge.gameId);
    if (game) {
      // Check if this is an external game
      if (isExternalGame(game)) {
        // For external games, launch immediately without showing popup
        requireAuth(() => {
          handleExternalGameClick(game);
        });
        return;
      }

      // Load shared popup state (used by both GameChallengePopup and GameWalkthroughModal fallback)
      setSelectedGame(game);
      setSelectedChallenge(challenge);
      setSelectedProgress(progress);

      // IMA-561: branch on walkthroughEnabled (IS-INV-025: optional chaining, no preflight fetch D-WALK-3)
      if (game.configuration?.walkthroughEnabled === true) {
        setWalkthroughActive(true);
      } else {
        setWalkthroughActive(false);
      }
    }
  };
  
  const handleArchivesClick = (gameId: string) => {
    // Use game ID directly in URL
    navigate(`/games/${gameId}/archive`);
  };

  // Handler for continuous game play
  const handleContinuousPlay = (gameUrl: string, challengeId: number) => {
    try {
      // Validate continuous game URL
      if (!gameUrl || typeof gameUrl !== 'string') {
        throw new GameLaunchError('Invalid game URL provided', 'unknown', 'CONFIGURATION');
      }

      // Validate challenge ID (should be 0 for continuous games)
      if (challengeId !== 0) {
        console.warn('[Games] Unexpected challenge ID for continuous game:', challengeId);
      }

      // Track analytics for continuous game launch
      posthog.capture('continuous_game_launch', {
        game_url: gameUrl,
        challenge_id: challengeId,
        is_authenticated: !!user
      });

      // Navigate to continuous game with challenge id 0
      requireAuth(() => {
        navigate(`/games/continuous/play/${challengeId}`, {
          state: {
            gameUrl,
            challengeId,
            isContinuous: true
          }
        });
      }, `/games/continuous/play/${challengeId}`);
    } catch (error) {
      console.error('[Games] Continuous game launch failed:', error);
      
      if (error instanceof GameLaunchError) {
        // Handle specific game launch errors
        console.error('Game launch error:', {
          gameId: error.gameId,
          errorType: error.errorType,
          message: error.message
        });

        // Show user-friendly error message
        // TODO: Replace with proper toast notification system
        alert(`Game launch failed: ${error.message}`);
      } else {
        console.error('Unexpected error during game launch:', error);
        alert('Unable to launch game. Please try again.');
      }
    }
  };
  
  // Process sections with games data (keeping external games in their sections)
  const { regularSections, premiumCTASection } = useMemo(() => {
    if (!sections || !games.length) return { regularSections: [], premiumCTASection: null };
    
    const regular: SectionWithGames[] = [];
    let premium: SectionWithGames | null = null;
    
    sections.forEach(section => {
      const sectionType = section.type;
      
      if (sectionType === 'cta_premium') {
        premium = section;
        return;
      }
      
      // Map section games to actual game objects (including external games).
      // Merge: fullGame carries challenge/progress/access-control data;
      // sectionGame carries surface fields (frontendUrl, previewTagline) from the sections API.
      const sectionGames = section.games.filter(sectionGame => {
        const fullGame = games.find(g => g.id === sectionGame.id);
        return fullGame && fullGame.isPublished;
      }).map(sectionGame => {
        const fullGame = games.find(g => g.id === sectionGame.id)!;
        return {
          ...fullGame,
          frontendUrl: sectionGame.frontendUrl,
          previewTagline: sectionGame.previewTagline,
          previewAnimationMs: sectionGame.previewAnimationMs,
        };
      });
      
      // Include sections that have any games (internal or external)
      if (sectionGames.length > 0) {
        regular.push({
          ...section,
          games: sectionGames
        });
      }
    });
    
    return { 
      regularSections: regular, 
      premiumCTASection: premium 
    };
  }, [sections, games]);
  
  // Show error state if games or sections failed to load
  if (error || sectionsError) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <div className="space-y-4">
            {error && <p className="text-red-600">Failed to load games: {error.message}</p>}
            {sectionsError && <p className="text-red-600">Failed to load sections: {sectionsError.message}</p>}
            <div className="space-x-2">
              {error && (
                <button 
                  onClick={() => refreshGames()}
                  disabled={loading}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Retrying Games...' : 'Retry Games'}
                </button>
              )}
              {sectionsError && (
                <button 
                  onClick={() => refetchSections()}
                  disabled={sectionsLoading}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sectionsLoading ? 'Retrying Sections...' : 'Retry Sections'}
                </button>
              )}
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }
  
  const handlePopupClose = () => {
    // Track analytics event
    if (selectedGame && selectedChallenge) {
      posthog.capture('game_popup_closed', {
        game_id: selectedGame.id,
        game_name: selectedGame.displayName,
        challenge_id: selectedChallenge.id,
        challenge_type: selectedGame.challengeType,
        is_authenticated: isAuthenticated
      });
    }

    setSelectedGame(null);
    setSelectedChallenge(null);
    setSelectedProgress(undefined);
    setWalkthroughActive(false);
  };

  // Handle locked challenge click - show premium upsell modal
  const handleLockedClick = (game: Game) => {
    setUpsellGame(game);
    setIsUpsellModalOpen(true);
  };

  const handleUpsellClose = () => {
    setIsUpsellModalOpen(false);
    setUpsellGame(null);
  };

  const handleUpsellUpgrade = () => {
    setIsUpsellModalOpen(false);
    setUpsellGame(null);
    navigate(`/subscription?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  };
  
  const handlePopupPlay = () => {
    if (selectedGame && selectedChallenge) {
      // Track analytics event
      posthog.capture('game_play_clicked', {
        game_id: selectedGame.id,
        game_name: selectedGame.displayName,
        challenge_id: selectedChallenge.id,
        challenge_type: selectedGame.challengeType,
        is_authenticated: isAuthenticated,
        from_popup: true
      });
      
      // Close popup first to prevent it from staying open
      handlePopupClose();
      
      // Check if this is an external game (defensive check - should not happen since external games bypass popup)
      if (isExternalGame(selectedGame)) {
        handleExternalGameClick(selectedGame);
        return;
      }
      
      // Navigate to the game URL with state for internal games
      const gameUrl = `/games/${selectedGame.id}/play/${selectedChallenge.id}`;
      // IS-INV-034: Access authorized by GameChallengePopup (canPlay from backend).
      // Do not use requireAuth — it blocks anonymous users with valid guest tokens.
      sessionStorage.setItem('justPlayedGame', selectedGame.id);
      navigate(gameUrl, {
        state: {
          game: selectedGame,
          challenge: selectedChallenge,
          progress: selectedProgress
        }
      });
    }
  };
  
  return (
    <PageContainer>
      <div className="space-y-3 md:space-y-3 xl:space-y-5">
        {/* Welcome Section */}
        <WelcomeSection
          userName={isUserLoaded ? (
            user?.username ||
            user?.firstName ||
            user?.fullName ||
            user?.primaryEmailAddress?.emailAddress?.split('@')[0]
          ) : undefined}
          sport={currentSport}
          gameCount={games.filter(g => g.challengeType !== 'external').length}
        />

        {/* Dynamic Sections from Database */}
        {(loading || sectionsLoading) ? (
          // Show loading skeletons while data loads
          <GameSection title="Loading sections...">
            <div className="space-y-5">
              <GameCard game={null} isLoading={true}>
                {/* Mobile skeleton */}
                <div className="block sm:hidden space-y-3">
                  {[...Array(7)].map((_, i) => (
                    <div key={i} className="h-24 bg-gray-300 rounded-lg animate-pulse" />
                  ))}
                </div>
                
                {/* Desktop horizontal scroll skeleton */}
                <div className="hidden sm:block">
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {/* Enhanced today challenge skeleton - wider */}
                    <div className="flex-shrink-0 w-80 h-32 bg-gray-300 rounded-lg animate-pulse" />
                    
                    {/* Historical challenges skeleton - compact width */}
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="flex-shrink-0 w-32 h-32 bg-gray-300 rounded-lg animate-pulse" />
                    ))}
                    
                    {/* Archive skeleton */}
                    <div className="flex-shrink-0 w-32 h-32 bg-gray-300 rounded-lg animate-pulse" />
                  </div>
                </div>
              </GameCard>
              <GameCard game={null} isLoading={true}>
                {/* Mobile skeleton */}
                <div className="block sm:hidden space-y-3">
                  {[...Array(7)].map((_, i) => (
                    <div key={i} className="h-24 bg-gray-300 rounded-lg animate-pulse" />
                  ))}
                </div>
                
                {/* Desktop horizontal scroll skeleton */}
                <div className="hidden sm:block">
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {/* Enhanced today challenge skeleton - wider */}
                    <div className="flex-shrink-0 w-80 h-32 bg-gray-300 rounded-lg animate-pulse" />
                    
                    {/* Historical challenges skeleton - compact width */}
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="flex-shrink-0 w-32 h-32 bg-gray-300 rounded-lg animate-pulse" />
                    ))}
                    
                    {/* Archive skeleton */}
                    <div className="flex-shrink-0 w-32 h-32 bg-gray-300 rounded-lg animate-pulse" />
                  </div>
                </div>
              </GameCard>
            </div>
          </GameSection>
        ) : (
          // Render sections from database
          // IMA-550: Branch on section.type (IS-INV-025: read-only; IS-INV-034: gameplay path unchanged)
          regularSections.map(section => {
            // Preview sections render PreviewCarousel — unknown future types fall through to gameplay default
            if (section.type === 'preview') {
              const previewGames = section.games;
              const rawIndex = previewIndexBySection[section.id] ?? 0;
              const sectionActiveIndex = previewGames.length > 0 ? Math.min(rawIndex, previewGames.length - 1) : 0;
              const setSectionActiveIndex = (i: number) =>
                setPreviewIndexBySection(prev => ({ ...prev, [section.id]: i }));

              const activePreviewGame = previewGames[sectionActiveIndex];
              const todayPreviewChallenge = activePreviewGame ? todayChallenges[activePreviewGame.id] : undefined;
              const canPreviewPlay = todayPreviewChallenge?.accessControl?.canPlay === true;
              // IS-INV-034: open modal, not direct play — anonymous users enter via handlePopupPlay
              const handlePreviewCTA = activePreviewGame && todayPreviewChallenge && canPreviewPlay
                ? () => handleChallengeClick(todayPreviewChallenge)
                : undefined;

              const dotNav = previewGames.length > 1 ? (
                <div className="flex items-center gap-1">
                  {previewGames.map((game, index) => (
                    <button
                      key={game.id}
                      onClick={() => setSectionActiveIndex(index)}
                      aria-label={`Go to ${game.displayName} slide`}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      <span
                        className={cn(
                          'block h-2 rounded-full transition-all duration-300',
                          index === sectionActiveIndex
                            ? 'w-6 bg-primary'
                            : 'w-2 bg-border hover:bg-muted-foreground'
                        )}
                      />
                    </button>
                  ))}
                </div>
              ) : undefined;
              return (
                <GameSection key={section.id} title={section.displayName} headerRight={dotNav}>
                  <PreviewCarousel
                    games={previewGames}
                    todayChallenges={todayChallenges}
                    activeIndex={sectionActiveIndex}
                    onActiveIndexChange={setSectionActiveIndex}
                    onCTAClick={handlePreviewCTA}
                  />
                </GameSection>
              );
            }

            // Gameplay default — pixel-for-pixel identical to prior behavior
            return (
              <GameSection
                key={section.id}
                title={section.displayName}
              >
                <div className="space-y-4 md:space-y-2 xl:space-y-4">
                  {section.games.map(game => {
                    // First check: External games get special widget (not affected by under-construction)
                    if (isExternalGame(game)) {
                      return (
                        <ExternalGameWidget
                          key={game.id}
                          game={game}
                        />
                      );
                    }

                    // Second check: Continuous/sim games not affected by under-construction
                    if (isContinuousGame(game)) {
                      return (
                        <GameCard
                          key={game.id}
                          game={game}
                        >
                          <PlayerProgressStream
                            gameId={game.id}
                            game={game}
                            userId={user?.id}
                            onChallengeClick={handleChallengeClick}
                            onLockedClick={handleLockedClick}
                            onArchivesClick={() => handleArchivesClick(game.id)}
                            onContinuousPlay={handleContinuousPlay}
                          />
                        </GameCard>
                      );
                    }

                    // Default: Regular time-based games (internal + auto-challenge)
                    // IMA-431: under_construction handled inside PlayerProgressStream today slot
                    // D39: onCardClick navigates to today's challenge once onTodayChallengeReady fires
                    const todayChallenge = todayChallenges[game.id];
                    // IS-INV-034: open modal, not direct play — anonymous users enter via handlePopupPlay
                    const handleCardClick = todayChallenge?.accessControl?.canPlay && game.contentAvailability !== 'under_construction'
                      ? () => handleChallengeClick(todayChallenge)
                      : undefined;
                    return (
                      <GameCard
                        key={game.id}
                        game={game}
                        onCardClick={handleCardClick}
                      >
                        <PlayerProgressStream
                          gameId={game.id}
                          game={game}
                          userId={user?.id}
                          onChallengeClick={handleChallengeClick}
                          onLockedClick={handleLockedClick}
                          onArchivesClick={() => handleArchivesClick(game.id)}
                          onContinuousPlay={handleContinuousPlay}
                          onTodayChallengeReady={(challenge) => handleTodayChallengeReady(game.id, challenge)}
                        />
                      </GameCard>
                    );
                  })}
                </div>
              </GameSection>
            );
          })
        )}

        {/* Premium CTA Widget - conditionally rendered from database */}
        {premiumCTASection && (
          <CTAWidget
            onButtonClick={() => {
              const subscriptionTarget = `/subscription?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`;
              requireAuth(() => { navigate(subscriptionTarget); }, subscriptionTarget);
            }}
          />
        )}

        {/* Coming Soon Section - Hard-coded Basketball and Football */}
        <ComingSoonSection />
      </div>
      
      {/* Walkthrough Modal — IMA-561: opens for walkthroughEnabled games (IS-INV-025, IS-INV-042) */}
      {selectedGame && selectedChallenge && walkthroughActive && (
        <GameWalkthroughModal
          game={selectedGame}
          playLabel={getChallengeButtonText({
            canPlay: (selectedChallenge as unknown as { accessControl?: { displayState?: string } }).accessControl?.displayState === 'playable',
            isCompleted: selectedProgress?.activityState === 'completed',
            isCurrentPeriod,
            challengeFrequency: selectedGame.challengeFrequency,
          })}
          isOpen={true}
          onClose={handlePopupClose}
          fallbackContext={{
            challenge: {
              ...selectedChallenge,
              displayState: (selectedChallenge as unknown as { accessControl?: { displayState?: 'playable' | 'locked' | 'display-only' } })
                .accessControl?.displayState ?? 'locked',
            },
            playerStats,
            progress: selectedProgress,
            isCurrentPeriod,
            onPlay: handlePopupPlay,
            isLoading: popupDataLoading,
            error: popupDataError?.message || null,
            onRetry: refetchPopupData,
            onArchivesClick: () => {
              handlePopupClose();
              handleArchivesClick(selectedGame.id);
            },
          }}
        />
      )}

      {/* Game Challenge Popup — unchanged for non-walkthrough games (AC-6, IS-INV-042) */}
      {selectedGame && selectedChallenge && !walkthroughActive && (
        <GameChallengePopup
          game={selectedGame}
          challenge={{
            ...selectedChallenge,
            // IS-INV-034: Promote accessControl.displayState to top-level so popup
            // can read challenge.displayState without re-implementing access logic.
            // accessControl is present at runtime (from ChallengeWithProgress) even
            // though selectedChallenge is typed as GameChallenge.
            displayState: (selectedChallenge as unknown as { accessControl?: { displayState?: 'playable' | 'locked' | 'display-only' } })
              .accessControl?.displayState ?? 'locked',
          }}
          playerStats={playerStats}
          progress={selectedProgress}
          isCurrentPeriod={isCurrentPeriod}
          onClose={handlePopupClose}
          onPlay={handlePopupPlay}
          isLoading={popupDataLoading}
          error={popupDataError?.message || null}
          onRetry={refetchPopupData}
          onArchivesClick={() => {
            // Close popup first, then navigate to archives
            handlePopupClose();
            handleArchivesClick(selectedGame.id);
          }}
        />
      )}

      {/* Premium Upsell Modal */}
      <PremiumUpsellModal
        isOpen={isUpsellModalOpen}
        onClose={handleUpsellClose}
        onUpgrade={handleUpsellUpgrade}
        signatureColor={upsellGame?.signatureColor || '#C5A572'}
      />
    </PageContainer>
  )
}